import {LoadingOutlined, QuestionCircleTwoTone} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Checkbox, Descriptions, Form, Input, message, Modal, Select, Spin, Switch, Tooltip} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AppContextContext, ItemUpdateProfileServiceContext, VulReportDefinitionServiceContext, VulViewServiceContext} from "../../Contexts";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {DocumentTitle} from "../DocumentTitle";
import VulView, {vulViewSystemLastComparator} from "../../domain/VulView";
import VulReportDefinition from "../../domain/VulReportDefinition";
import * as globalStyles from "../App.module.css";
import * as styles from "./VulReportDefinitionDetail.module.css";
import * as tackStyles from "../vulsourceitem/Tack.module.css";
import _ from "lodash";
import {capitalize, formatDateTime} from "../../utils/FormatUtils";
import * as descriptionStyles from "../SelectItemDescription.module.css";
import MarkdownContent from "../common/MarkdownContent";
import {Permission} from "../../domain/auth/Permission";
import ItemUpdateProfile, {itemUpdateProfileSystemLastComparator} from "../../domain/ItemUpdateProfile";
import {describeUpdatedItemsBehaviour, formatUpdatedItemsBehaviour, UpdatedItemsBehaviour} from "../../domain/UpdatedItemsBehaviour";
import {matchesSearchText} from "../../utils/StringUtils";
import {Feature} from "../../domain/Feature";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";
import {definedValues} from "../../utils/SelectOptionUtils";
import AiReportSummarizerSelect from "../aireportsummarizer/AiReportSummarizerSelect";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function VulReportDefinitionDetail() {
    const appContext = useContext(AppContextContext);
    const vulReportDefinitionService = useContext(VulReportDefinitionServiceContext);
    const vulViewService = useContext(VulViewServiceContext);
    const itemUpdateProfileService = useContext(ItemUpdateProfileServiceContext);
    const navigate = useNavigate();
    const {definitionId} = useParams<{ definitionId: string }>();
    const [editMode, setEditMode] = useState(false);
    const [definition, setDefinition] = useState<VulReportDefinition>();
    const [editForm] = useForm();
    const [vulViews, setVulViews] = useState<VulView[]>();
    const [itemUpdateProfiles, setItemUpdateProfiles] = useState<ItemUpdateProfile[]>();
    const autoGenerateAiSummary = Form.useWatch("autoGenerateAiSummary", editForm);
    const reportUpdatedItemsBehaviour = Form.useWatch("reportUpdatedItemsBehaviour", editForm);
    const canUseAiSummary = canDefinitionUseAiSummary(definition, appContext.user.hasFeature(Feature.AI));

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        vulViewService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => {
            setVulViews(value.data?.sort(vulViewSystemLastComparator));
        });

        itemUpdateProfileService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => {
            setItemUpdateProfiles(value.data?.sort(itemUpdateProfileSystemLastComparator));
        });

        reload();
    }, [])

    return (
        <DocumentTitle title={definition?.name}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to={"/definitions/vul-reports"}>Vulnerability report definitions</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{definition?.name}</Breadcrumb.Item>
                </Breadcrumb>

                <Spin spinning={definition === undefined} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{definition?.name}</h1>

                    {appContext.user.hasPermission(Permission.REPORT__MANAGE) &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                            <Button danger={true}
                                    title={"Delete"}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to delete this report definition? This will also delete all reports created from this definition.",
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: () => onDeleteConfirm()
                                        });
                                    }}>Delete</Button>

                            <Button title={"Clone"} onClick={() => navigate(`/definitions/vul-reports/clone/${definition.id}`)}>Clone</Button>
                        </div>
                    }

                    {renderEditForm()}

                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                        {appContext.user.isSystemAdmin() && <Descriptions.Item label={"Organization"}>
                            <Link to={`/organizations/${definition?.organization?.id}`}>{definition?.organization?.name}</Link>
                        </Descriptions.Item>}
                        <Descriptions.Item label={"Vulnerability views"}>
                            {definition?.vulViews?.map(view => (
                                <div><Link key={view.id} to={`/vul-source-items/views/${view.id}`}>{view.name}</Link></div>
                            ))}
                        </Descriptions.Item>
                        <Descriptions.Item label={"Days"}>{renderDays(definition?.days)}</Descriptions.Item>
                        <Descriptions.Item label={"Hours"}>{definition?.times?.join(", ")}</Descriptions.Item>
                        <Descriptions.Item label={"Time zone"}>{definition?.timeZone}</Descriptions.Item>
                        <Descriptions.Item label={"Report updated items"}>{formatUpdatedItemsBehaviour(definition?.reportUpdatedItemsBehaviour)}</Descriptions.Item>
                        {definition?.reportUpdatedItemsBehaviour === UpdatedItemsBehaviour.PROFILE &&
                            <Descriptions.Item label={"Item update profile"}>
                                <Link to={`/definitions/item-update-profiles/${definition.itemUpdateProfile?.id}`}>{definition.itemUpdateProfile?.name}</Link>
                            </Descriptions.Item>
                        }
                        <Descriptions.Item label={"Enabled"}><Checkbox checked={definition?.enabled} disabled={true}/></Descriptions.Item>
                        <Descriptions.Item label={"Generate empty report"}><Checkbox checked={definition?.generateEmptyReport} disabled={true}/></Descriptions.Item>
                        {canUseAiSummary &&
                            <Descriptions.Item label={"Auto-generate AI summary"}>
                                <Checkbox checked={definition?.autoGenerateAiSummary} disabled={true}/>
                            </Descriptions.Item>
                        }
                        {canUseAiSummary && definition?.autoGenerateAiSummary &&
                            <Descriptions.Item label={"AI report summarizer"}>
                                {definition?.aiReportSummarizer?.name || <i>Global default</i>}
                            </Descriptions.Item>
                        }
                        <Descriptions.Item label={"E-mail"}>{definition?.email}</Descriptions.Item>

                        {appContext.user.isSystemAdmin() && <Descriptions.Item label={"Created at"}>{formatDateTime(definition?.createdAt)}</Descriptions.Item>}
                    </Descriptions>
                </Spin>
            </>
        </DocumentTitle>
    )

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit of vulnerability report definition</h3>

                <Form {...layout} form={editForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onFinishEdit}>

                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."}
                        ]}>
                        <Input autoFocus={true} maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"vulViews"}
                        label={"Views"}
                        rules={[{required: true, message: "View is required."}]}>
                        <SelectAllMultiSelect
                            selectAllValues={definedValues(vulViews, vulView => vulView.id)}
                            filterOption={(inputValue, option) => matchesSearchText(inputValue, option?.label)}
                            optionLabelProp={"label"}
                            popupMatchSelectWidth={350}
                            virtual={false}
                            listHeight={300}
                        >
                            {vulViews?.map(vulView =>
                                <Select.Option key={vulView.id} value={vulView.id} label={vulView.name}>
                                    {vulView.owner === 'SYSTEM' && <div className={`${tackStyles.tack} ${tackStyles['tack-seal']}`}><span className={tackStyles['tack-text']} color={"#440084"}>SEAL</span></div>}
                                    {vulView.owner === 'ORGANIZATION' && <div className={`${tackStyles.tack} ${tackStyles['tack-org']}`}><span className={tackStyles['tack-text']} color={"#87d068"}>ORG</span></div>}

                                    <div className={`${tackStyles.item}`}>
                                        <div className={descriptionStyles['name-container']}>
                                            <span className={descriptionStyles.name}>{vulView.name}</span>
                                            {vulView.description && <Tooltip title={<MarkdownContent children={vulView.description}/>}><QuestionCircleTwoTone/></Tooltip>}
                                        </div>
                                        <div className={descriptionStyles.description}>{vulView.description}</div>
                                    </div>
                                </Select.Option>
                            )}
                        </SelectAllMultiSelect>
                    </Form.Item>

                    <Form.Item
                        name={"days"}
                        label={"Days"}>
                        <Checkbox.Group className={styles.days}>
                            <Checkbox value={"MONDAY"}>Monday</Checkbox>
                            <Checkbox value={"TUESDAY"}>Tuesday</Checkbox>
                            <Checkbox value={"WEDNESDAY"}>Wednesday</Checkbox>
                            <Checkbox value={"THURSDAY"}>Thursday</Checkbox>
                            <Checkbox value={"FRIDAY"}>Friday</Checkbox>
                            <Checkbox value={"SATURDAY"}>Saturday</Checkbox>
                            <Checkbox value={"SUNDAY"}>Sunday</Checkbox>
                        </Checkbox.Group>
                    </Form.Item>

                    <Form.Item
                        name={"times"}
                        label={"Hours"}>
                        <Checkbox.Group
                            className={styles.times}
                            options={_.range(24).map(index => String(index).padStart(2, '0') + ":00")}
                        />
                    </Form.Item>

                    <Form.Item
                        name={"timeZone"}
                        label={"Time zone"}
                        initialValue={new Intl.DateTimeFormat().resolvedOptions().timeZone}
                        rules={[{required: true, message: "Time zone is required."}]}>
                        <Select
                            options={Intl.supportedValuesOf("timeZone").map(value => ({
                                label: value,
                                value
                            }))}
                            showSearch={true}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name={"email"}
                        label={"E-mail"}
                        extra={"Comma separated list of email addresses."}>
                        <Input maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"reportUpdatedItemsBehaviour"}
                        label={"Report updated items"}
                        rules={[{required: true, message: "Report updated items setting is required."}]}>
                        <Select
                            optionLabelProp={"label"}
                            popupMatchSelectWidth={500}>
                            {Object.values(UpdatedItemsBehaviour).map(value =>
                                <Select.Option key={value} value={value} label={formatUpdatedItemsBehaviour(value)}>
                                    <span>{formatUpdatedItemsBehaviour(value)}</span>
                                    <span className={descriptionStyles.description}> - {describeUpdatedItemsBehaviour(value)}</span>
                                </Select.Option>
                            )}
                        </Select>
                    </Form.Item>

                    {reportUpdatedItemsBehaviour === UpdatedItemsBehaviour.PROFILE &&
                        <Form.Item
                            name={"itemUpdateProfile"}
                            label={"Item update profile"}
                            rules={[{required: true, message: "Item update profile is required."}]}>
                            <Select
                                showSearch={true}
                                optionFilterProp={"label"}
                                options={itemUpdateProfiles?.map(profile => ({
                                    value: profile.id,
                                    label: profile.name
                                }))}
                            />
                        </Form.Item>
                    }

                    <Form.Item
                        name={"enabled"}
                        label={"Enabled"}
                        valuePropName={"checked"}>
                        <Switch/>
                    </Form.Item>

                    <Form.Item
                        name={"generateEmptyReport"}
                        label={"Generate empty report"}
                        valuePropName={"checked"}>
                        <Switch/>
                    </Form.Item>

                    {canUseAiSummary &&
                        <Form.Item
                            name={"autoGenerateAiSummary"}
                            label={"Auto-generate AI summary"}
                            valuePropName={"checked"}>
                            <Switch/>
                        </Form.Item>
                    }

                    {canUseAiSummary &&
                        <Form.Item
                            name={"aiReportSummarizerId"}
                            hidden={!autoGenerateAiSummary}
                            label={"AI report summarizer"}
                            extra={"Leave empty to use the global default."}>
                            <AiReportSummarizerSelect
                                loadOptions={editMode && autoGenerateAiSummary === true}
                                organizationId={definition?.organization?.id}
                                currentSummarizer={definition?.aiReportSummarizer}
                            />
                        </Form.Item>
                    }

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Save"}</Button>
                        <Button onClick={() => setEditMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        )
    }

    function renderDays(days?: string[]) {
        if (days?.length == 7) {
            return <i>every day</i>;
        }

        return days?.map(d => capitalize(d.toLowerCase())).join(", ");
    }

    function onFinishEdit(values: any) {
        vulReportDefinitionService.update(definitionId, normalizeFormValues(values))
            .then(
                () => {
                    message.success(<>Vulnerability report definition updated.</>);

                    setEditMode(false);

                    reload();
                },
                error => serverViolationsHolder.handleServerError(error, editForm)
            );
    }

    function onDeleteConfirm() {
        vulReportDefinitionService.delete(definition)
            .then(() => {
                message.success(<>Vulnerability report definition <b>{definition.name}</b> deleted.</>);

                navigate(`/definitions/vul-reports`);
            });
    }

    function reload() {
        vulReportDefinitionService.get(definitionId).then(definition => {
            setDefinition(definition);

            editForm.setFieldsValue({
                ...definition,
                vulViews: definition.vulViews.map(value => value.id),
                itemUpdateProfile: definition.itemUpdateProfile?.id,
                aiReportSummarizerId: definition.aiReportSummarizer?.id
            });
        });
    }

}

function normalizeFormValues(values: any) {
    return {
        name: values.name,
        vulViews: values.vulViews,
        days: values.days,
        times: values.times,
        timeZone: values.timeZone,
        email: values.email,
        reportUpdatedItemsBehaviour: values.reportUpdatedItemsBehaviour,
        itemUpdateProfile: values.reportUpdatedItemsBehaviour === UpdatedItemsBehaviour.PROFILE ? values.itemUpdateProfile || null : null,
        enabled: values.enabled,
        generateEmptyReport: values.generateEmptyReport,
        autoGenerateAiSummary: values.autoGenerateAiSummary === true,
        ...("aiReportSummarizerId" in values ? {aiReportSummarizerId: values.autoGenerateAiSummary ? values.aiReportSummarizerId || null : null} : {})
    };
}

function canDefinitionUseAiSummary(definition: VulReportDefinition | undefined, userCanUseAiSummary: boolean) {
    if (!userCanUseAiSummary) {
        return false;
    }

    if (!definition?.organization?.features) {
        return userCanUseAiSummary;
    }

    return definition.organization.features.includes(Feature.AI);
}

export default VulReportDefinitionDetail;
